import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiAward, FiStar, FiUsers, FiMessageCircle, FiHeart, FiShield, 
  FiZap, FiActivity, FiGlobe, FiMapPin, FiCheckCircle, FiFlag,
  FiTarget, FiAward as FiTrophy, FiUserCheck, FiMessageSquare,
  FiEdit, FiCamera, FiTrendingUp, FiUnlock, FiEdit2
} from 'react-icons/fi';
import { 
  GiSoccerBall, GiBasketballBall, GiTennisBall, GiPoliceBadge, 
  GiRank3, GiChampions, GiTargetPrize, GiPowerLightning
} from 'react-icons/gi';
import Navbar from '../components/Navbar';
import { userAPI, matchAPI } from '../services/api';
import { useAuthContext } from '../context/AuthContext';

const Profile = () => {
  const navigate = useNavigate();
  const { login, token } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [userMatches, setUserMatches] = useState([]);
  const [stats, setStats] = useState({ hostedCount: 0, joinedCount: 0, messageCount: 0 });
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    preferredSport: '',
    skillLevel: '',
    location: '',
    profileImage: '',
    bio: '',
  });

  const [originalData, setOriginalData] = useState(formData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const localUser = JSON.parse(localStorage.getItem('user'));
      if (!localUser) {
        navigate('/login');
        return;
      }
      
      const [userRes, matchesRes] = await Promise.all([
        userAPI.getProfile(),
        matchAPI.getUserMatches(localUser._id || localUser.id)
      ]);

      if (userRes.success) {
        const user = userRes.user;
        setProfileUser(user);
        setStats(userRes.stats || { hostedCount: 0, joinedCount: 0, messageCount: 0 });
        const data = {
          fullName: user.fullName || '',
          email: user.email || '',
          preferredSport: user.preferredSport || '',
          skillLevel: user.skillLevel || '',
          location: user.location || '',
          profileImage: user.profileImage || '',
          bio: user.bio || '',
        };
        setFormData(data);
        setOriginalData(data);
      }
      
      if (matchesRes.success) {
        setUserMatches(matchesRes.matches);
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const userInitials = formData.fullName
    ? formData.fullName.split(' ').map((n) => n[0]).join('').toUpperCase()
    : '??';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setOriginalData(formData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(originalData);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.fullName || !formData.email || !formData.location) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const response = await userAPI.updateProfile({
        fullName: formData.fullName,
        preferredSport: formData.preferredSport ? formData.preferredSport.toLowerCase() : '',
        skillLevel: formData.skillLevel ? formData.skillLevel.toLowerCase() : '',
        location: formData.location,
        profileImage: formData.profileImage,
        bio: formData.bio,
      });

      if (response.success && response.user) {
        // Update global auth state with new user data
        login(response.user, token);
        
        // Update profile display
        setProfileUser(response.user);
        
        // Update form data to match server response
        const updatedFormData = {
          fullName: response.user.fullName || '',
          email: response.user.email || '',
          preferredSport: response.user.preferredSport || '',
          skillLevel: response.user.skillLevel || '',
          location: response.user.location || '',
          profileImage: response.user.profileImage || '',
          bio: response.user.bio || '',
        };
        setFormData(updatedFormData);
        setOriginalData(updatedFormData);
        
        setIsEditing(false);
        setError('');
      } else {
        setError(response.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black min-h-screen">
      <Navbar hideAuthButtons={true} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded mb-8 flex items-center justify-between">
            <p className="font-bold uppercase tracking-widest text-xs">{error}</p>
            <button onClick={loadProfile} className="text-white hover:underline text-xs font-bold">RETRY</button>
          </div>
        )}

        {/* Loading State */}
        {loading && !profileUser && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-500 font-bold uppercase tracking-widest text-sm text-center">
              Warming up the engine...<br/>
              <span className="text-[10px] opacity-50">Syncing with PlayConnect servers</span>
            </p>
          </div>
        )}

        {/* Profile Content */}
        {!loading && profileUser && (
          <>
            {/* Profile Header */}
            <div className="bg-gray-900 border-l-4 border-red-500 p-8 rounded-sm mb-8 flex flex-col md:flex-row items-center md:items-start gap-8 relative group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 blur-3xl rounded-full -mr-16 -mt-16 group-hover:bg-red-500/10 transition-colors duration-700" />
              
              {!isEditing && (
                <button
                  onClick={handleEdit}
                  className="absolute top-4 right-4 md:top-8 md:right-8 flex items-center gap-2 text-white border-2 border-white px-4 py-2 rounded hover:bg-white hover:text-black transition font-semibold text-xs md:text-sm z-20"
                >
                  <FiEdit2 className="w-4 h-4" />
                  <span className="hidden md:inline">EDIT PROFILE</span>
                </button>
              )}

              <div className="flex-shrink-0 relative z-10">
                <div className="w-32 h-32 rounded-full border-4 border-red-500 bg-gray-800 flex items-center justify-center overflow-hidden relative group">
                  {formData.profileImage ? (
                    <img src={formData.profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-5xl font-bold text-red-500">{userInitials}</span>
                  )}
                  {isEditing && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <FiCamera className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-grow text-center md:text-left mt-4 md:mt-0 relative z-10">
                <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">{formData.fullName}</h1>
                {formData.location && (
                  <div className="flex items-center justify-center md:justify-start gap-2 text-gray-400 text-sm mb-4">
                    <FiMapPin className="text-red-500" />
                    {formData.location}
                  </div>
                )}
                
                {!isEditing && (
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 md:gap-6 text-white font-bold mt-6 border-t border-gray-800 pt-6">
                    <div className="text-center">
                      <div className="text-xl md:text-2xl text-red-500 font-black">{profileUser.followers?.length || 0}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-[0.1em] md:tracking-[0.2em] font-bold">Followers</div>
                    </div>
                    <div className="text-center border-x border-gray-800 px-4 md:px-6">
                      <div className="text-xl md:text-2xl text-red-500 font-black">{profileUser.following?.length || 0}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-[0.1em] md:tracking-[0.2em] font-bold">Following</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl md:text-2xl text-red-500 font-black">{userMatches.length}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-[0.1em] md:tracking-[0.2em] font-bold">Matches</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* View/Edit Content */}
            {isEditing ? (
              <div className="bg-gray-900 border-t-4 border-red-500 p-8 rounded-sm mb-8 animate-in fade-in slide-in-from-top-4 duration-300">
                <h2 className="text-xl font-bold text-red-500 uppercase tracking-widest mb-8 flex items-center gap-2">
                  <div className="w-4 h-[2px] bg-red-500" /> Edit Profile
                </h2>
                <form onSubmit={handleSave} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2">Full Name</label>
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:border-red-500 transition text-sm outline-none" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2">Email</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:border-red-500 transition text-sm outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2">Sport</label>
                      <select name="preferredSport" value={formData.preferredSport} onChange={handleChange} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:border-red-500 transition text-sm outline-none">
                        <option value="football">Football</option>
                        <option value="basketball">Basketball</option>
                        <option value="tennis">Tennis</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2">Skill</label>
                      <select name="skillLevel" value={formData.skillLevel} onChange={handleChange} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:border-red-500 transition text-sm outline-none">
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2">Location</label>
                    <input type="text" name="location" value={formData.location} onChange={handleChange} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:border-red-500 transition text-sm outline-none" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2">Image URL</label>
                    <input type="url" name="profileImage" value={formData.profileImage} onChange={handleChange} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:border-red-500 transition text-sm outline-none" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2">Bio</label>
                    <textarea name="bio" value={formData.bio} onChange={handleChange} rows="4" className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:border-red-500 transition text-sm outline-none resize-none" />
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button type="button" onClick={handleCancel} className="flex-1 border border-gray-700 text-gray-400 font-bold py-3 px-4 rounded hover:bg-gray-800 transition text-xs uppercase tracking-widest">Cancel</button>
                    <button type="submit" disabled={loading} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded transition text-xs uppercase tracking-widest shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                      {loading ? 'SAVING...' : 'SAVE CHANGES'}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 animate-in fade-in duration-700">
                <div className="bg-gray-900 p-6 border border-gray-800 rounded-sm">
                  <h3 className="text-gray-500 text-[10px] font-bold uppercase tracking-[0.3em] mb-4">Bio</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{profileUser.bio || "No bio added yet."}</p>
                </div>
                <div className="bg-gray-900 p-6 border border-gray-800 rounded-sm">
                  <h3 className="text-gray-500 text-[10px] font-bold uppercase tracking-[0.3em] mb-4">Preferences</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                      <span className="text-gray-500 text-xs">Sport</span>
                      <span className="text-white font-bold capitalize">{profileUser.preferredSport || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-xs">Skill Level</span>
                      <span className="text-white font-bold capitalize">{profileUser.skillLevel || 'Not set'}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-black border-2 border-gray-900 p-8 md:col-span-2 relative">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4 border-b-2 border-gray-900 pb-6">
                    <div>
                      <h3 className="text-white text-2xl font-black uppercase tracking-tighter mb-1">System Achievements</h3>
                      <p className="text-red-500 text-[10px] uppercase tracking-[0.3em] font-bold">Protocol Status: Active</p>
                    </div>
                    <div className="bg-gray-900 px-6 py-3 border border-gray-800">
                      <span className="text-white text-2xl font-black italic mr-2">
                        {Math.round(([
                          new Date(profileUser.createdAt) < new Date('2026-06-01'),
                          stats.hostedCount >= 1, stats.hostedCount >= 5, stats.hostedCount >= 20,
                          stats.joinedCount >= 1, stats.joinedCount >= 5, stats.joinedCount >= 20,
                          stats.messageCount >= 1, stats.messageCount >= 50, stats.messageCount >= 200,
                          (profileUser.followers?.length || 0) >= 1, (profileUser.followers?.length || 0) >= 10, (profileUser.followers?.length || 0) >= 50,
                          (profileUser.following?.length || 0) >= 10, (profileUser.following?.length || 0) >= 50,
                          !!profileUser.profileImage, !!profileUser.bio, !!profileUser.location,
                          profileUser.skillLevel === 'advanced',
                          profileUser.preferredSport === 'football', profileUser.preferredSport === 'basketball', profileUser.preferredSport === 'tennis',
                          new Date() - new Date(profileUser.createdAt) > 7 * 24 * 60 * 60 * 1000,
                          new Date() - new Date(profileUser.createdAt) > 30 * 24 * 60 * 60 * 1000,
                          true
                        ].filter(Boolean).length / 25) * 100)}%
                      </span>
                      <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Complete</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {[
                      { icon: <FiAward />, title: 'Founder', desc: 'Early User', condition: new Date(profileUser.createdAt) < new Date('2026-06-01'), progress: 100 },
                      { icon: <FiTarget />, title: 'Host I', desc: 'Host 1 Match', condition: stats.hostedCount >= 1, current: stats.hostedCount, target: 1 },
                      { icon: <FiTrendingUp />, title: 'Host II', desc: 'Host 5 Matches', condition: stats.hostedCount >= 5, current: stats.hostedCount, target: 5 },
                      { icon: <FiTrophy />, title: 'Host III', desc: 'Host 20 Matches', condition: stats.hostedCount >= 20, current: stats.hostedCount, target: 20 },
                      { icon: <FiUnlock />, title: 'Player I', desc: 'Join 1 Match', condition: stats.joinedCount >= 1, current: stats.joinedCount, target: 1 },
                      { icon: <FiActivity />, title: 'Player II', desc: 'Join 5 Matches', condition: stats.joinedCount >= 5, current: stats.joinedCount, target: 5 },
                      { icon: <GiTargetPrize />, title: 'Player III', desc: 'Join 20 Matches', condition: stats.joinedCount >= 20, current: stats.joinedCount, target: 20 },
                      { icon: <FiMessageSquare />, title: 'Comm I', desc: '1 Message', condition: stats.messageCount >= 1, current: stats.messageCount, target: 1 },
                      { icon: <FiMessageCircle />, title: 'Comm II', desc: '50 Messages', condition: stats.messageCount >= 50, current: stats.messageCount, target: 50 },
                      { icon: <GiPowerLightning />, title: 'Comm III', desc: '200 Messages', condition: stats.messageCount >= 200, current: stats.messageCount, target: 200 },
                      { icon: <FiUserCheck />, title: 'Social I', desc: '1 Follower', condition: (profileUser.followers?.length || 0) >= 1, current: (profileUser.followers?.length || 0), target: 1 },
                      { icon: <FiStar />, title: 'Social II', desc: '10 Followers', condition: (profileUser.followers?.length || 0) >= 10, current: (profileUser.followers?.length || 0), target: 10 },
                      { icon: <FiZap />, title: 'Social III', desc: '50 Followers', condition: (profileUser.followers?.length || 0) >= 50, current: (profileUser.followers?.length || 0), target: 50 },
                      { icon: <FiUsers />, title: 'Network I', desc: 'Follow 10', condition: (profileUser.following?.length || 0) >= 10, current: (profileUser.following?.length || 0), target: 10 },
                      { icon: <FiGlobe />, title: 'Network II', desc: 'Follow 50', condition: (profileUser.following?.length || 0) >= 50, current: (profileUser.following?.length || 0), target: 50 },
                      { icon: <FiCamera />, title: 'Visual', desc: 'Profile Image', condition: !!profileUser.profileImage },
                      { icon: <FiEdit />, title: 'Intel', desc: 'Bio Added', condition: !!profileUser.bio },
                      { icon: <FiMapPin />, title: 'Base', desc: 'Loc Added', condition: !!profileUser.location },
                      { icon: <FiShield />, title: 'Elite', desc: 'Adv Skill', condition: profileUser.skillLevel === 'advanced' },
                      { icon: <GiSoccerBall />, title: 'Striker', desc: 'Football', condition: profileUser.preferredSport === 'football' },
                      { icon: <GiBasketballBall />, title: 'Baller', desc: 'Basketball', condition: profileUser.preferredSport === 'basketball' },
                      { icon: <GiTennisBall />, title: 'Ace', desc: 'Tennis', condition: profileUser.preferredSport === 'tennis' },
                      { icon: <FiCheckCircle />, title: 'Week 1', desc: '1 Week Old', condition: new Date() - new Date(profileUser.createdAt) > 7 * 24 * 60 * 60 * 1000 },
                      { icon: <GiRank3 />, title: 'Month 1', desc: '1 Month Old', condition: new Date() - new Date(profileUser.createdAt) > 30 * 24 * 60 * 60 * 1000 },
                      { icon: <GiPoliceBadge />, title: 'Alpha', desc: 'Beta Tester', condition: true }
                    ].map((badge, i) => {
                      const progress = badge.progress !== undefined 
                        ? badge.progress 
                        : Math.min(100, Math.round(((badge.current || 0) / (badge.target || 1)) * 100));

                      return (
                        <div key={i} className={`group relative p-4 border transition-all duration-200 ${badge.condition ? 'bg-gray-900 border-gray-800 hover:border-red-500' : 'bg-black border-gray-900 opacity-40'}`}>
                          <div className={`text-xl mb-3 transition-transform ${badge.condition ? 'text-red-500' : 'text-gray-700'}`}>
                            {badge.icon}
                          </div>
                          <div className="text-white font-black text-[10px] uppercase tracking-widest mb-4">{badge.title}</div>
                          
                          {/* Industrial Progress Bar */}
                          <div className="w-full h-1 bg-gray-800">
                            <div 
                              className={`h-full ${badge.condition ? 'bg-red-500' : 'bg-gray-600'} transition-all duration-700`} 
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>

                          {/* Hover Overlay - Industrial Style */}
                          <div className="absolute inset-0 bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2 text-center pointer-events-none">
                             <div className="text-white font-black text-[10px] uppercase tracking-[0.2em] mb-1">{badge.desc}</div>
                             <div className="text-black font-black text-[8px] uppercase tracking-widest bg-white px-2 py-0.5">{progress}% SYNC</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="h-1 bg-gradient-to-r from-red-500 via-red-500 to-transparent"></div>
    </div>
  );
};

export default Profile;
